import React from "react";
import axios, { AxiosResponse } from "axios";
import moment from 'moment';
import { AlertColor, Button, Checkbox, FormControl, FormControlLabel, FormLabel, MenuItem, Paper, Radio, RadioGroup, TextField, useMediaQuery} from "@mui/material";
import Title from "../../component/Title";
import { DropDownComboOption } from "../../component/GenericFilter";
import { globalTheme } from "../../../common/theme";
import { useConstructor } from "../../../common/UseConstructor";
import { useAppSelector } from "../../../common/hooks";
import { RootState } from "../../../common/Store";
import { CommonProps } from "../../component/MainContents";
import { getUserPreferences, renewToken } from "../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import { useNavigate } from "react-router-dom";
import { RegionCombo } from "../regions/RegionAccessories";
import { BranchInfo } from "../branches/BranchAccessories";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { PurchaseOrderAnalysisViewer, PurchaseOrderAnalysisViewerHandle } from "./PurchaseOrderAnalysisViewer";
import NumberFormat from "react-number-format";
import { ValidateNumber } from "../../../common/CommonFunctions";
import { NumberTypes } from "../../../models/enums/NumberType";

export function PurchaseOrderAnalysis(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);
	const [regionComboData, setRegionComboData] = React.useState<DropDownComboOption[]>([]);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

    const viewerModalRef = React.useRef<PurchaseOrderAnalysisViewerHandle>(null);

	const navigate = useNavigate();

    const style = { ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');

	const [selectedRegion, setSelectedRegion] = React.useState<string>("");
    const [dateType, setDateType] = React.useState<number>(0);
	const [fromDate, setFromDate] = React.useState<Date>(new Date());
    const [toDate, setToDate] = React.useState<Date>(new Date());
    const [shipmentType, setShipmentType] = React.useState<number>(0);
    const [reportType, setReportType] = React.useState<number>(0);
	const [showSalesman, setShowSalesman] = React.useState<boolean>(false);
	const [isExport, setIsExport] = React.useState<boolean>(false);
    const [exRateUsd, setExRateUsd] = React.useState<number>(90);
    const [exRateHkd, setExRateHkd] = React.useState<number>(11.5);
    const [exRateRmb, setExRateRmb] = React.useState<number>(10);
    const [usdExRateErrorText, setUsdExRateErrorText] = React.useState<string>("");
    const [hkdExRateErrorText, setHkdExRateErrorText] = React.useState<string>("");
    const [rmbExRateErrorText, setRmbExRateErrorText] = React.useState<string>("");

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	useConstructor(() => Initialise())
    function Initialise() {
        setLoading(true);
		renewToken();
		let lastMonth = new Date();
		lastMonth.setMonth((new Date()).getMonth() - 1);
		setFromDate(lastMonth);
		
		axios.all([
			axios.get<any[]>(`Api/V1/Region/Combo`, {
				headers: {
					'Content-Encoding': 'gzip'
				}
			}),                
			axios.get<BranchInfo[]>(`Api/V1/Branch`, {
				headers: {
					'Content-Encoding': 'gzip'
				}
			})
		]).then(axios.spread((...responses) => {
			const regionComboResult: AxiosResponse<RegionCombo[]> = responses[0];
			const branchInfoResult: AxiosResponse<BranchInfo[]> = responses[1];

			let loadedRegions: DropDownComboOption[] = [];
			branchInfoResult.data.map(b => {
				if (regionComboResult.data.find(r => r.regionName === b.region))
					loadedRegions.push({ name: b.companyName1, value: b.region });
			});
			setRegionComboData(loadedRegions);

			let preferences = getUserPreferences();
			if (preferences)
			{
				setSelectedRegion(preferences.defaultRegion);
			}
			setLoading(false);
		})).catch((error) => {
			setHasError(true);
			setLoading(false);
			console.log(error);
			showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
		})

    }

    function Preview() {
		viewerModalRef.current?.Initialise(selectedRegion, dateType, fromDate, toDate, shipmentType, reportType, exRateUsd, exRateHkd, exRateRmb);
	}

    return (
        <div className={style.PageMain}>
			<Title pageTitle="Purchase Order Summary Report" />
				<Paper variant="outlined" className={style.searchBar} >
					<div style={{ display: 'flex'}}>
						<div className={style.ModalFields} style={{width: '100%'}}>
							<TextField style={{ left: 10, width: 80, marginTop: 10, paddingTop: "5px", paddingBottom: "5px" }}
								onChange={(e) => {setSelectedRegion(e.target.value)}}
								value = {selectedRegion}
								select
								label="Region"
								size="small"
							>
								{regionComboData.map((item)=> (
									<MenuItem key={item.value} value={item.value}>
										{item.value}
									</MenuItem>
								))}
							</TextField>
							<div style={{ width: 10 }}></div>
							<div style={{ alignItems: 'start', marginLeft: 20, marginTop: 15 }}>
								<RadioGroup
									key="dateType"
									row
									value={dateType}
									defaultValue={dateType}
									name="date-type"
									onChange={(e) => setDateType(parseInt(e.target.value))}
								>
									<FormControlLabel value="0" control={<Radio />} label="Create Date" />
									<FormControlLabel value="1" control={<Radio />} label="Actual Date" />
								</RadioGroup>
							</div>
							<div style={{ width: 10 }}></div>
							<LocalizationProvider dateAdapter={AdapterDateFns}>
								<DatePicker
									key="fromDate"
									label="From"
									inputFormat="yyyy/MM/dd"
									mask="____/__/__"
									value={fromDate}
									onChange={(newValue) => setFromDate(newValue ?? new Date())}
									renderInput={(params) => 
										<TextField {...params} size="small"
											style={{ width: "160px", marginLeft: 10, marginTop: 15 }}
										/>
									}
								/>
							</LocalizationProvider>
							<div style={{ width: 10 }}></div>
							<LocalizationProvider dateAdapter={AdapterDateFns}>
								<DatePicker
									key="toDate"
									label="To"
									inputFormat="yyyy/MM/dd"
									mask="____/__/__"
									value={toDate}
									onChange={(newValue) => setToDate(newValue ?? new Date())}
									renderInput={(params) => 
										<TextField {...params} size="small"
											style={{ width: "160px", marginLeft: 10, marginTop: 15 }}
										/>
									}
								/>
							</LocalizationProvider>
							<div style={{ width: 10 }}></div>
							<RadioGroup
								key="shipmentType"
								value={shipmentType}
								defaultValue={shipmentType}
								name="shipment-type"
								onChange={(e) => setShipmentType(parseInt(e.target.value))}
								style={{ marginLeft: 10, marginTop: 15 }}
							>
								<FormControlLabel value="0" control={<Radio />} label="All" />
								<FormControlLabel value="1" control={<Radio />} label="Fully Shipped" />
								<FormControlLabel value="2" control={<Radio />} label="Partial Shipped" />
								<FormControlLabel value="3" control={<Radio />} label="Non Shipped" />
							</RadioGroup>
							<div style={{ width: 10 }}></div>
							<div style={{ alignItems: 'start', marginTop: 15 }}>
								<FormControlLabel
									control={<Checkbox checked={showSalesman} onChange={(e) => {
											setShowSalesman(e.target.checked);
											if (!e.target.checked)
											{
												setReportType(0); //Standard
											}
											else
											{
												if (isExport)
												{
													setReportType(2); //Standard with salesman column and export
												}
												else
												{
													setReportType(1); //Standard with only salesman column
												}
											}
										}}/>}
									label="Show Salesman"
								/>						
							</div>	
							<div style={{ alignItems: 'start', marginTop: 15 }}>
								<FormControlLabel
									control={<Checkbox checked={isExport} onChange={(e) => {
											setIsExport(e.target.checked);
											if (!showSalesman)
											{
												setReportType(0); //Standard
											}
											else
											{
												if (e.target.checked)
												{
													setReportType(2); //Standard with salesman column and export
												}
												else
												{
													setReportType(1); //Standard with only salesman column
												}
											}
										}}/>}
									label="Export"
								/>							
							</div>
							<div style={{ width: 10 }}></div>
							<div style={{ alignItems: 'start', marginTop: 15, width: 110 }}>
								<NumberFormat
								 	style={{ marginBottom: 15 }}
									customInput={TextField}
									value={exRateUsd}
									label="USD Ex. Rate"
									error={usdExRateErrorText !== ""}
									helperText={usdExRateErrorText}
									decimalScale={1}
									thousandSeparator
									onValueChange={(e) => {
									if (e.value !== "")
									{
										let msg = ValidateNumber(e.value, NumberTypes.FLOAT, true);
										setUsdExRateErrorText(msg);
										if (msg === "")
											setExRateUsd(parseFloat(e.value));
									}
									}}
									size="small"
									fullWidth
								/>
								<NumberFormat
								 	style={{ marginBottom: 15 }}
									 customInput={TextField}
									value={exRateHkd}
									label="HKD Ex. Rate"
									error={hkdExRateErrorText !== ""}
									helperText={hkdExRateErrorText}
									decimalScale={1}
									thousandSeparator
									onValueChange={(e) => {
									if (e.value !== "")
									{
										let msg = ValidateNumber(e.value, NumberTypes.FLOAT, true);
										setHkdExRateErrorText(msg);
										if (msg === "")
											setExRateHkd(parseFloat(e.value));
									}
									}}
									size="small"
									fullWidth
								/>
								<NumberFormat
								 	style={{ marginBottom: 15 }}
									 customInput={TextField}
									value={exRateRmb}
									label="RMB Ex. Rate"
									error={rmbExRateErrorText !== ""}
									helperText={rmbExRateErrorText}
									decimalScale={1}
									thousandSeparator
									onValueChange={(e) => {
									if (e.value !== "")
									{
										let msg = ValidateNumber(e.value, NumberTypes.FLOAT, true);
										setRmbExRateErrorText(msg);
										if (msg === "")
											setExRateRmb(parseFloat(e.value));
									}
									}}
									size="small"
									fullWidth
								/>
							</div>
							<div style={{ flexGrow: 1 }}></div>
							<div style={{ width: '100px', paddingTop: "12px", paddingLeft: "8px" }}>
								<Button type="submit" aria-controls="filter-menu" aria-haspopup="true" variant="contained" color="secondary" fullWidth onClick={(e)=> Preview()}>
									Preview
								</Button>
							</div>
							<div style={{ width: 10 }}></div>					

						</div>
					</div>
				</Paper>
				<div className={style.PageContent}>
			</div>

			<PurchaseOrderAnalysisViewer ref={viewerModalRef} onClose={()=>{}}/>

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

